import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { playSound } from '../utils/sound';
import { queueStatusUpdate, syncQueue, getTotalQueued } from '../utils/offlineQueue';
import api from '../utils/api';
import { devLog } from '../utils/logger';
import {
  KitchenHeader,
  KitchenHero,
  KitchenOfflineBanner,
  KitchenTabs,
  PrepBanner,
  BatchSuggestions,
  StockWarnings,
  KitchenTickets,
  KitchenStockTab,
} from './kitchen/KitchenDisplayParts';

export default function KitchenDisplay() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('tickets');
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [prepSuggestions, setPrepSuggestions] = useState([]);
  const [prepDismissed, setPrepDismissed] = useState(false);
  const [prepExpanded, setPrepExpanded] = useState(false);
  const [thresholds, setThresholds] = useState({ new_warning: 3, new_urgent: 5, new_critical: 8, pickup_warning: 1, pickup_urgent: 3, pickup_critical: 5 });
  const prevCriticalRef = useRef(new Set());
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('wc_sound') !== 'off');
  const [offlineCount, setOfflineCount] = useState(getTotalQueued());
  const [syncing, setSyncing] = useState(false);
  const prevConnectedRef = useRef(false);

  const playSoundIfEnabled = useCallback((type) => { if (soundEnabled) playSound(type); }, [soundEnabled]);
  const toggleSound = () => { const next = !soundEnabled; setSoundEnabled(next); localStorage.setItem('wc_sound', next ? 'on' : 'off'); };

  const fetchOrders = useCallback(async () => { try { const { data } = await api.get('/orders?status=active'); setOrders(data); } catch (err) { devLog.error('Failed to fetch orders:', err); } }, []);
  const fetchMenu = useCallback(async () => { try { const { data } = await api.get('/menu'); setMenu(data); } catch (err) { devLog.error('Failed to fetch menu:', err); } }, []);
  const fetchPrep = useCallback(async () => { try { const { data } = await api.get('/kitchen/prep-suggestions'); setPrepSuggestions(data.suggestions || []); } catch (err) { devLog.error('Prep fetch failed:', err); } }, []);

  useEffect(() => { fetchOrders(); fetchMenu(); fetchPrep(); api.get('/settings/thresholds').then(r => setThresholds(r.data)).catch(err => devLog.error('Thresholds fetch failed:', err)); }, [fetchOrders, fetchMenu, fetchPrep]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { const t = setInterval(fetchPrep, 60000); return () => clearInterval(t); }, [fetchPrep]);

  const onWsMessage = useCallback((msg) => {
    if (msg.type === 'new_order') { playSoundIfEnabled('new_order'); fetchOrders(); fetchPrep(); }
    if (msg.type === 'order_status' || msg.type === 'order_cancelled') { fetchOrders(); fetchPrep(); }
    if (msg.type === 'menu_update' || msg.type === 'item_86') fetchMenu();
    if (msg.type === 'settings_update') { if (msg.thresholds) setThresholds(msg.thresholds); }
    if (msg.type === 'daily_reset') { fetchOrders(); fetchMenu(); setPrepDismissed(false); }
  }, [fetchOrders, fetchMenu, fetchPrep, playSoundIfEnabled]);
  const wsConnected = useWebSocket(onWsMessage);

  // Auto-sync when connection restores
  useEffect(() => {
    if (wsConnected && !prevConnectedRef.current && offlineCount > 0) {
      setSyncing(true);
      syncQueue(api).then(result => {
        if (result.synced > 0) playSoundIfEnabled('sync_complete');
        setOfflineCount(getTotalQueued());
        setSyncing(false);
        fetchOrders();
      });
    }
    prevConnectedRef.current = wsConnected;
  }, [wsConnected, offlineCount, playSoundIfEnabled, fetchOrders]);

  // Warning sound when orders cross critical threshold
  useEffect(() => {
    const criticals = orders.filter(o => {
      const mins = (o.status === 'ready' && o.completed_at)
        ? (now - new Date(o.completed_at).getTime()) / 60000
        : (now - new Date(o.created_at).getTime()) / 60000;
      const th = (o.status === 'ready') ? thresholds.pickup_critical : thresholds.new_critical;
      return mins > th;
    });
    if (criticals.length > 0) {
      const ids = new Set(criticals.map(o => o.id));
      if ([...ids].some(id => !prevCriticalRef.current.has(id))) playSoundIfEnabled('warning');
      prevCriticalRef.current = ids;
    } else { prevCriticalRef.current = new Set(); }
  }, [now, orders, thresholds, playSoundIfEnabled]);

  useEffect(() => {
    const urgent = orders.some(o => (o.status === 'new' && (now - new Date(o.created_at).getTime()) / 60000 > 5) ||
      (o.status === 'ready' && o.completed_at && (now - new Date(o.completed_at).getTime()) / 60000 > 3));
    document.title = urgent ? (document.title.startsWith('!!') ? 'Wildcraft' : '!! ORDERS !!') : 'Wildcraft';
  }, [now, orders]);

  const startOrder = async (id) => {
    try { await api.put(`/orders/${id}/status`, { status: 'in_progress' }); }
    catch (e) { if (!e.response) { queueStatusUpdate(id, 'in_progress'); setOfflineCount(getTotalQueued()); playSoundIfEnabled('offline_queued'); } }
  };
  const completeOrder = async (id) => {
    try { await api.put(`/orders/${id}/status`, { status: 'ready' }); }
    catch (e) { if (!e.response) { queueStatusUpdate(id, 'ready'); setOfflineCount(getTotalQueued()); playSoundIfEnabled('offline_queued'); } }
  };
  const toggle86 = async (id) => { try { await api.put(`/menu/${id}/86`); } catch (err) { devLog.error('Toggle 86 failed:', err); } };

  const [stockEdit, setStockEdit] = useState({});
  const [stockNotify, setStockNotify] = useState(null);
  const updateStock = async (id, qty) => {
    try { await api.put(`/menu/${id}`, { servings_available: Math.max(0, qty) }); fetchMenu(); setStockNotify('Updated'); setTimeout(() => setStockNotify(null), 2000); } catch (err) { devLog.error('Stock update failed:', err); }
  };
  const saveStockManual = async (id) => {
    const val = parseInt(stockEdit[id]);
    if (isNaN(val) || val < 0) return;
    try { await api.put(`/menu/${id}`, { servings_available: val }); fetchMenu(); setStockEdit(s => { const n = {...s}; delete n[id]; return n; }); setStockNotify(`Set to ${val}`); setTimeout(() => setStockNotify(null), 2000); } catch (err) { devLog.error('Stock save failed:', err); }
  };

  const getTimer = (order) => {
    if (order.status === 'ready' && order.completed_at) return { mins: (now - new Date(order.completed_at).getTime()) / 60000, type: 'pickup' };
    return { mins: (now - new Date(order.created_at).getTime()) / 60000, type: 'wait' };
  };

  const urgencyLevel = (t) => {
    const m = t.mins; const th = thresholds;
    if (t.type === 'pickup') {
      if (m > th.pickup_critical) return 'critical';
      if (m > th.pickup_urgent) return 'urgent';
      if (m > th.pickup_warning) return 'warning';
      return 'normal';
    }
    if (m > th.new_critical) return 'critical';
    if (m > th.new_urgent) return 'urgent';
    if (m > th.new_warning) return 'warning';
    return 'normal';
  };

  const ticketClass = (level) => ({
    critical: 'ticket-critical',
    urgent: 'ticket-urgent pulse-border',
    warning: 'ring-2 ring-[#B8860B]',
    normal: ''
  }[level] || '');

  const timerClr = (level) => ({
    critical: 'text-[#FF3333] font-black text-2xl timer-shake',
    urgent: 'text-orange-600 font-bold text-xl',
    warning: 'text-[#B8860B] font-bold',
    normal: 'text-[#7D8B69]'
  }[level] || 'text-[#7D8B69]');

  const fmt = (m) => `${Math.floor(m)}:${Math.floor((m % 1) * 60).toString().padStart(2, '0')}`;

  const batches = useMemo(() => {
    const nw = orders.filter(o => o.status === 'new');
    if (nw.length < 2) return [];
    const map = {};
    for (const o of nw) for (const i of o.items) { if (!map[i.name]) map[i.name] = []; map[i.name].push({ num: o.order_number, qty: i.qty }); }
    return Object.entries(map).filter(([, l]) => l.length >= 2).map(([n, l]) => ({ item: n, total: l.reduce((s, o) => s + o.qty, 0), orders: l })).sort((a, b) => b.total - a.total);
  }, [orders]);

  const lowStock = useMemo(() => menu.filter(m => !m.is_86 && m.servings_available > 0 && m.servings_available <= m.low_threshold), [menu]);
  const soldOut = useMemo(() => menu.filter(m => m.is_86 || m.servings_available <= 0), [menu]);
  const sorted = useMemo(() => [...orders].sort((a, b) => { const av = a.status === 'ready' ? 0 : a.status === 'new' ? 1 : 2; const bv = b.status === 'ready' ? 0 : b.status === 'new' ? 1 : 2; return av !== bv ? av - bv : new Date(a.created_at) - new Date(b.created_at); }), [orders]);
  const kitchenStats = useMemo(() => {
    const readyCount = orders.filter(o => o.status === 'ready').length;
    const newCount = orders.filter(o => o.status === 'new').length;
    const cookingCount = orders.filter(o => o.status === 'in_progress').length;
    const urgentCount = orders.filter(o => ['urgent', 'critical'].includes(urgencyLevel(getTimer(o)))).length;
    const totalMinutes = orders.reduce((sum, order) => sum + getTimer(order).mins, 0);
    const avgMinutes = orders.length > 0 ? totalMinutes / orders.length : 0;
    return { readyCount, newCount, cookingCount, urgentCount, avgMinutes };
  }, [orders, now, thresholds]);

  const isSuperAdmin = ['super_admin', 'admin'].includes(user.role);

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex flex-col" data-testid="kitchen-display">
      <KitchenHeader isSuperAdmin={isSuperAdmin} user={user} logout={logout} toggleSound={toggleSound} soundEnabled={soundEnabled} wsConnected={wsConnected} activeCount={orders.length} />
      <KitchenOfflineBanner wsConnected={wsConnected} offlineCount={offlineCount} syncing={syncing} />
      <KitchenTabs tab={tab} setTab={setTab} fetchMenu={fetchMenu} orderCount={orders.length} />

      {tab === 'tickets' && (
        <div className="space-y-4 px-4 pb-6 sm:px-6">
          <PrepBanner prepSuggestions={prepSuggestions} prepDismissed={prepDismissed} prepExpanded={prepExpanded} setPrepExpanded={setPrepExpanded} setPrepDismissed={setPrepDismissed} />
          <BatchSuggestions batches={batches} />
          <StockWarnings lowStock={lowStock} soldOut={soldOut} toggle86={toggle86} />
          <KitchenTickets orders={sorted} getTimer={getTimer} urgencyLevel={urgencyLevel} ticketClass={ticketClass} timerClr={timerClr} fmt={fmt} startOrder={startOrder} completeOrder={completeOrder} />
        </div>
      )}

      {tab === 'stock' && (
        <>
          <KitchenHero activeCount={orders.length} readyCount={kitchenStats.readyCount} newCount={kitchenStats.newCount} cookingCount={kitchenStats.cookingCount} urgentCount={kitchenStats.urgentCount} avgMinutes={kitchenStats.avgMinutes} wsConnected={wsConnected} tab={tab} />
          <KitchenStockTab menu={menu} stockNotify={stockNotify} stockEdit={stockEdit} setStockEdit={setStockEdit} updateStock={updateStock} saveStockManual={saveStockManual} />
        </>
      )}
    </div>
  );
}

